import { action, noop, withAssign } from '@reatom/framework'
import i18next from 'i18next'

import { pipe, Result } from '@blade/std-lib'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { reatomRequest } from '@root/shared/lib'
import { showErrorSnackbarAction } from '@root/shared/ui/error-snackbar'
import { showConfirmDialogAction } from '@root/shared/ui-kit'

export const cancelAllOrdersRequestAction = action(
  async (
    ctx,
    {
      network,
      currencies,
    }: { network: Network.Network; currencies?: Array<string> },
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.trade.tradeLimitOrdersCancelAllCreate, [
        {
          network: Network.toDto(network),
          tokenAddresses: currencies,
        },
      ]),
    )

    return pipe(result, Result.mapError(ApiError.fallbackToUnknown))
  },
  'cancelAllOrdersRequestAction',
)

export const allOrdersCancelledEvent = action(noop, 'allOrdersCancelledEvent')

export const reatomCancelAllOrders = () => {
  const requestState = reatomRequest({
    execAction: cancelAllOrdersRequestAction,
    prepare: (ctx, currencies: Array<string> | undefined) => {
      const network = ctx.get(selectedNetworkAtom)
      return { network, currencies }
    },
    // eslint-disable-next-line @reatom/unit-naming-rule
    name: 'cancelAllOrdersRequest',
    onResult: (ctx, result) => {
      if (Result.isSuccess(result)) {
        allOrdersCancelledEvent(ctx)
        return
      }

      showErrorSnackbarAction(ctx, result.failure)
    },
  })

  const confirmCancellationAction = action(
    async (ctx, currencies: Array<string> | undefined) => {
      const { confirmed } = await ctx.schedule(() =>
        showConfirmDialogAction(ctx, {
          title: i18next.t('order.cancel-all-confirm.title'),
          describedBy: i18next.t('order.cancel-all-confirm.description'),
          description: i18next.t('order.cancel-all-confirm.description'),
          confirmLabel: i18next.t('order.cancel-all'),
          cancelLabel: i18next.t('common.dismiss'),
        }),
      )

      if (!confirmed) {
        return
      }

      requestState.execute(ctx, currencies)
    },
    'confirmCancellationAction',
  )

  return requestState.pipe(
    withAssign(() => ({
      cancelAll: confirmCancellationAction,
    })),
  )
}
